/**
 * useApplicant — find (or lazily create) the `role: "applicant"` person for a
 * case. Every wizard step works against this single person record.
 *
 * The Go API doesn't auto-create the applicant on /cases creation, so the
 * first wizard mount calls POST /v1/cases/{cid}/persons {role: "applicant"}.
 * Subsequent mounts hit GET /persons and pick the applicant.
 */

import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { persons } from "@/lib/endpoints";
import type {
  PersonResponse,
  UpdatePersonRequest,
} from "@/lib/api-types";

export function useApplicant(caseId: string | undefined) {
  const qc = useQueryClient();
  const ensureRef = useRef(false);

  const list = useQuery({
    queryKey: ["case", caseId, "persons"],
    queryFn: () => persons.list(caseId!),
    enabled: !!caseId,
  });

  const applicant = list.data?.items.find((p) => p.role === "applicant") ?? null;

  const create = useMutation({
    mutationFn: () =>
      persons.create(caseId!, { role: "applicant" }),
    onSuccess: (p: PersonResponse) => {
      qc.setQueryData(["case", caseId, "persons"], (prev: { items: PersonResponse[]; total: number } | undefined) =>
        prev
          ? { items: [...prev.items, p], total: prev.total + 1 }
          : { items: [p], total: 1 },
      );
    },
  });

  // Auto-provision applicant once the list query settles without one.
  useEffect(() => {
    if (!caseId) return;
    if (list.isLoading) return;
    if (applicant) return;
    if (ensureRef.current) return;
    ensureRef.current = true;
    create.mutate();
  }, [caseId, list.isLoading, applicant, create]);

  const update = useMutation({
    mutationFn: (req: UpdatePersonRequest) =>
      persons.update(caseId!, applicant!.id, req),
    onSuccess: (p) => {
      qc.setQueryData(["case", caseId, "persons"], (prev: { items: PersonResponse[]; total: number } | undefined) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((x) => (x.id === p.id ? p : x)),
            }
          : prev,
      );
      // Re-evaluate readiness after every save.
      qc.invalidateQueries({ queryKey: ["case", caseId, "evaluation"] });
      // The Person row contributes to the rendered snapshot — flag the
      // packet as outdated so the review page surfaces "Re-generate".
      qc.invalidateQueries({ queryKey: ["case", caseId, "packet-status"] });
    },
  });

  return {
    applicant,
    isLoading: list.isLoading || create.isPending,
    update,
    error: list.error ?? create.error,
  };
}
